import { z } from 'zod';

// Welche REP-Bausteine der Digest zeigt (NOT-5: "welche Kennzahlen/Grafiken").
export const DIGEST_SECTIONS = ['rep1', 'rep2', 'rep3', 'rep4', 'rep5'] as const;
export const DigestSectionSchema = z.enum(DIGEST_SECTIONS);
export type DigestSection = z.infer<typeof DigestSectionSchema>;

export const DigestSettingsSchema = z.object({
  enabled: z.boolean(),
  // Versand-Wochentag nach JS Date.getDay(): 0=So … 6=Sa (Default 1 = Montag).
  weekday: z.number().int().min(0).max(6),
  time: z.string().regex(/^\d{2}:\d{2}$/), // "07:00"
  timezone: z.string(), // IANA, z.B. "Europe/Berlin"
  recipient_user_ids: z.array(z.string().uuid()),
  sections: z.array(DigestSectionSchema),
  // #55: leere Liste ⇒ alle aktiven Pläne zur Laufzeit. So berichtet der Digest ohne
  // Konfiguration weiter, statt still leerzulaufen.
  test_plan_ids: z.array(z.string().uuid()),
  // #129 Tester-Digest (werktags, ein Schalter je Projekt). Lesesicht: beide Felder immer da.
  tester_digest_enabled: z.boolean(),
  tester_digest_time: z.string().regex(/^\d{2}:\d{2}$/),
});
export type DigestSettings = z.infer<typeof DigestSettingsSchema>;

// #129: Schreibsicht (PUT). Die Tester-Felder sind hier OHNE Vorgabe optional: ein veralteter
// Client (Tab vor dem Deploy geöffnet) kennt sie nicht und darf einen gespeicherten Schalter nicht
// still auf „aus" zurücksetzen. Fehlen sie, bleiben die Spalten beim UPDATE unberührt.
export const DigestSettingsInputSchema = DigestSettingsSchema.extend({
  tester_digest_enabled: DigestSettingsSchema.shape.tester_digest_enabled.optional(),
  tester_digest_time: DigestSettingsSchema.shape.tester_digest_time.optional(),
});
export type DigestSettingsInput = z.infer<typeof DigestSettingsInputSchema>;

// #160: Zustände eines Wochenbericht-Laufs (`digest_runs.status`). `running` steht, solange der Lauf
// beansprucht und noch nicht abgeschlossen ist; `interrupted` setzt der Start-Hook, wenn ein Lauf
// beim Neustart des Servers noch lief.
export const DIGEST_RUN_STATUSES = ['running', 'sent', 'failed', 'empty', 'interrupted'] as const;
export type DigestRunStatus = (typeof DIGEST_RUN_STATUSES)[number];

export const DigestRunSummarySchema = z.object({
  digest_run_id: z.string().uuid(),
  window: z.string(), // ISO-Woche "2026-W42"
  status: z.enum(DIGEST_RUN_STATUSES),
  recipient_count: z.number().int(),
  subject: z.string(),
  generated_at: z.string().datetime(),
});
export type DigestRunSummary = z.infer<typeof DigestRunSummarySchema>;

// #160: Antwort von „Jetzt senden". Der Versand läuft im Hintergrund; das Ergebnis steht danach in
// der Berichtshistorie unter `digest_run_id`. `skipped` legt keinen Lauf an (`reason` sagt warum).
export const DigestSendResponseSchema = z.object({
  result: z.enum(['started', 'already_running', 'skipped']),
  digest_run_id: z.string().uuid().nullable(),
  reason: z.enum(['mail_off', 'no_settings']).nullable(),
});
export type DigestSendResponse = z.infer<typeof DigestSendResponseSchema>;

// Reines View-Model, das render.ts zu HTML macht (aus report-metrics gefüttert).
export const DigestDataSchema = z.object({
  project_name: z.string(),
  customer_name: z.string(),
  plan_name: z.string(),
  window: z.string(),
  sections: z.array(DigestSectionSchema),
  executedPct: z.number(),
  passedPct: z.number(),
  openBlockerOnInScope: z.number().int(),
  totalOpenDefects: z.number().int(),
  coveragePct: z.number(),
  goNoGo: z.enum(['go', 'no_go', 'not_configured']),
  etaProjectedDate: z.string().nullable(),
  // #97: Delta zum Vortages-Snapshot (loadYesterdaySnapshot) — null, wenn kein STRIKT-gestern-
  // Snapshot existiert (Tag 1 nach Rollout, Tick-Lücke). Dieselbe Grundmenge wie exec_passed/
  // open_blocker im Snapshot: planweit (in-scope), offene Blocker projektweit über color_token.
  sinceYesterday: z
    .object({
      passed: z.number().int(),
      openBlocker: z.number().int(),
    })
    .nullable(),
});
export type DigestData = z.infer<typeof DigestDataSchema>;
